import React from 'react'
import { Redirect, useLocation, useParams } from 'react-router-dom'

import { useGetPermanentArtboardShortIdQuery } from 'gqlTypes'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import NotFoundView from 'views/NotFoundView'
import GenericErrorView from 'views/GenericErrorView'
import { routes } from 'constants/routes'
import { isShortId } from 'utils'

const ArtboardRedirect = () => {
  const location = useLocation()
  const { artboardShortId, shareID } = useParams()

  const { data, loading, error } = useGetPermanentArtboardShortIdQuery({
    variables: {
      shortId: artboardShortId,
      shareIdentifier: shareID,
    },
  })

  /**
   *   As a result of this issue:
   *   https://github.com/sketch-hq/Cloud/issues/681
   *
   *   We are preventing the back-end of receiving an incorrect "shortID"
   *   by verifying the format. If the "shortId" format is correct they
   *   should see a 404 page.
   */
  if (!isShortId(artboardShortId)) {
    return <NotFoundView />
  }

  /**
   * A special case when route `/s/:shareID/a` is mixed with
   * route `/s/:shareID/:artboardShortId`. This is not a valid route
   * so it is good to handle it by redirecting back to the document
   */
  if (artboardShortId === 'a') {
    return (
      <Redirect
        to={{
          pathname: routes.SHARE_VIEW.create({ shareID }),
          search: location.search,
        }}
      />
    )
  }

  /**
   * Another special case is `/s/:shareID/unsubscribe`, used in emails
   * to unfollow automatically from an specific share
   */
  if (artboardShortId === 'unsubscribe') {
    return (
      <Redirect
        to={{
          pathname: routes.SHARE_VIEW.create({ shareID }),
          state: {
            unsubscribe: true,
          },
        }}
      />
    )
  }

  if (loading) return <DynamicLoadingPage />

  const permanentId = data?.artboard?.permanentArtboardShortId

  if (!permanentId) {
    return <NotFoundView />
  }

  if (error) {
    return <GenericErrorView error={error} />
  }

  return (
    <Redirect
      to={{
        pathname: location.pathname.replace(
          `/${artboardShortId}`,
          `/a/${permanentId}`
        ),
        search: location.search,
        state: location.state,
      }}
    />
  )
}

export default ArtboardRedirect
